import { Table } from 'antd';
import { ColumnsType, ColumnType } from 'antd/es/table';
import classNames from 'classnames';
import React from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { TInvoice, TLineItem } from './type';
import { CSVLink } from 'react-csv';
import { useScreenSize } from '../../../hooks/useScreenSize';
import { useNotification } from '../../../hooks/useNotification';
import { getInvoiceDetails } from '../../../api/invoice';
import { setLoading } from '../../../redux/slices/appInfo';
import { ICNoneData } from '../../../icons';

const WrapperTable = styled.div`
    .ant-table-thead > tr > th {
        background-color: white;
    }
    .data-row:nth-child(odd) {
        background-color: #f7f8fa;
    }
    .ant-table-column-sorters .ant-table-column-sorter-inner {
        .anticon {
            display: none;
        }
    }

    .ant-table-expanded-row.ant-table-expanded-row-level-1 {
        td.ant-table-cell:first-child {
            padding: 0 !important;
        }
    }
`;

type LineItemsFoliosProps = {
    invoiceSelected: TInvoice | undefined;
};

export const LineItemsFolios: React.FC<LineItemsFoliosProps> = ({
    invoiceSelected,
}) => {
    const size = useScreenSize();
    const mobileMode = size === 'xs' || size === 'sm';
    const dispatch = useDispatch();
    const { showError } = useNotification();
    const [lineItems, setLineItems] = React.useState<TLineItem[]>([]);

    const loadInvoiceDetails = React.useCallback(
        async (invoiceId: string) => {
            try {
                dispatch(setLoading(true));
                const result = await getInvoiceDetails(invoiceId);
                if (result.status === 200) {
                    setLineItems(result.data?.data?.invoice?.line_items ?? []);
                }
            } catch (error: any) {
                showError('Error', error?.message);
            } finally {
                dispatch(setLoading(false));
            }
        },
        [dispatch, showError]
    );

    React.useEffect(() => {
        if (invoiceSelected?.id) {
            loadInvoiceDetails(invoiceSelected.id);
        }
    }, [invoiceSelected]);

    const columns: ColumnsType<TLineItem> = [
        {
            title: 'No',
            dataIndex: 'No',
            key: 'No',
            align: 'center' as const,
            render: (value, record, index) => {
                return index + 1;
            },
        },
        {
            title: 'TrxDate',
            dataIndex: 'TrxDate',
            key: 'TrxDate',
        },
        {
            title: 'Description',
            dataIndex: 'Description',
            key: 'Description',
        },
        {
            title: 'Reference',
            dataIndex: 'Reference',
            key: 'Reference',
        },
        {
            title: 'Quantity',
            dataIndex: 'Quantity',
            key: 'Quantity',
        },
        {
            title: 'UnitPrice',
            dataIndex: 'UnitPrice',
            key: 'UnitPrice',
        },
        {
            title: 'NetAmount',
            dataIndex: 'NetAmount',
            key: 'NetAmount',
        },
        {
            title: 'taxRate',
            dataIndex: 'taxRate',
            key: 'taxRate',
        },
        {
            title: 'taxAmount',
            dataIndex: 'taxAmount',
            key: 'taxAmount',
        },
        {
            title: 'GrossAmount',
            dataIndex: 'GrossAmount',
            key: 'GrossAmount',
        },
        {
            title: 'subtotal',
            dataIndex: 'subtotal',
            key: 'subtotal',
        },
        {
            title: 'totalExcludeTax',
            dataIndex: 'totalExcludeTax',
            key: 'totalExcludeTax',
        },
        {
            title: 'SubGroup',
            dataIndex: 'SubGroup',
            key: 'SubGroup',
        },
        {
            title: 'Currency',
            dataIndex: 'Currency',
            key: 'Currency',
        },
        {
            title: 'FinDmlSeqNo',
            dataIndex: 'FinDmlSeqNo',
            key: 'FinDmlSeqNo',
        },
        {
            title: 'GuestAccountDebit',
            dataIndex: 'GuestAccountDebit',
            key: 'GuestAccountDebit',
        },
        {
            title: 'Group',
            dataIndex: 'Group',
            key: 'Group',
        },
        {
            title: 'TrxNo',
            dataIndex: 'TrxNo',
            key: 'TrxNo',
        },
        {
            title: 'Product',
            dataIndex: 'Product',
            key: 'Product',
        },
        {
            title: 'TrxCode',
            dataIndex: 'TrxCode',
            key: 'TrxCode',
        },
        {
            title: 'TrxType',
            dataIndex: 'TrxType',
            key: 'TrxType',
        },
        {
            title: 'TrxDateTime',
            dataIndex: 'TrxDateTime',
            key: 'TrxDateTime',
        },
        {
            title: 'TranActionId',
            dataIndex: 'TranActionId',
            key: 'TranActionId',
        },
        {
            title: 'LocalTrxDateTime',
            dataIndex: 'LocalTrxDateTime',
            key: 'LocalTrxDateTime',
        },
        {
            title: 'CashierId',
            dataIndex: 'CashierId',
            key: 'CashierId',
        },
        {
            title: 'OrgResvNameId',
            dataIndex: 'OrgResvNameId',
            key: 'OrgResvNameId',
        },
        {
            title: 'ArrangementCode',
            dataIndex: 'ArrangementCode',
            key: 'ArrangementCode',
        },
    ].map((col: ColumnType<TLineItem>) => {
        col.title = (
            <div className="text-body-bold text-high-em">
                {col.title as string}
            </div>
        );
        col.showSorterTooltip = false;
        return col;
    });

    return (
        <WrapperTable className="flex flex-col gap-[20px] w-full overflow-hidden h-full items-start box-border">
            <div
                className={classNames([
                    'flex flex-col w-full h-full rounded-[12px] overflow-hidden shadow-e-03',
                    {
                        'bg-white': !mobileMode,
                    },
                ])}
            >
                <Table
                    key="lineItems"
                    className={classNames('white-header w-full')}
                    columns={columns}
                    dataSource={lineItems}
                    rowClassName="data-row"
                    rowKey="TrxNo"
                    scroll={{ x: 'max-content' }}
                    locale={{
                        emptyText: <EmptyTable />,
                    }}
                    pagination={false}
                />
            </div>
        </WrapperTable>
    );
};

const EmptyTable: React.FC = () => {
    return (
        <div className="my-6 flex flex-col gap-[5px]">
            <div className="flex justify-center">
                <ICNoneData />
            </div>
            <div className="text-standard-bold text-high-em">No data found</div>
        </div>
    );
};
